/**
 * React-Anbindung der Figuren-Schicht: welche Figur spricht gerade, welche
 * gibt es, und wie wechselt ein Host sie.
 *
 * Bewusst getrennt von AgentSessionContext: Der Sitzungs-Context ist an
 * EINEN Controller gebunden und wird beim Figurenwechsel neu aufgebaut —
 * die Figuren-Auswahl muss diesen Wechsel überleben.
 */

import { createContext, useContext, useEffect } from 'react'
import type { AgentPersona } from './AgentPersona'
import type { AgentPersonaRegistry } from './AgentPersonaRegistry'

export interface AgentPersonaContextValue {
  /** Die Figur, deren Controller der AgentSessionContext gerade liefert. */
  persona: AgentPersona
  /** Alle Figuren des Mandanten (Auswahl, Debug, Tests). */
  personas: AgentPersonaRegistry
  /** Figur wechseln — beendet eine laufende Sitzung der alten Figur. */
  activatePersona: (id: string) => void
}

export const AgentPersonaContext = createContext<AgentPersonaContextValue | null>(null)

export function useAgentPersona(): AgentPersonaContextValue {
  const ctx = useContext(AgentPersonaContext)
  if (!ctx) throw new Error('useAgentPersona must be used inside <TschaukoAgentRoot>')
  return ctx
}

/**
 * Host-Hook: „solange diese Seite steht, spricht Figur X."
 *
 * Beim Verlassen wird NICHT automatisch zurückgeschaltet — der Besucher
 * soll ein laufendes Gespräch mitnehmen können. Ein anderer Host, der eine
 * andere Figur verlangt, wechselt beim Mounten ohnehin.
 */
export function useAgentPersonaHost(personaId: string): AgentPersona {
  const { persona, activatePersona } = useAgentPersona()
  useEffect(() => { activatePersona(personaId) }, [personaId, activatePersona])
  return persona
}
